/**
 * parentLinks.ts — De ouder↔kind-koppeling op de server verbreken.
 *
 * Het ouderportaal had een "Ontkoppelen"-knop die alleen de lokale opslag
 * aanpaste. Op de server bleef de koppeling staan, dus:
 *
 *   - bij de volgende keer inloggen kwam het kind gewoon weer terug;
 *   - de ouder hield via RLS toegang tot alle gegevens van dat kind;
 *   - en omdat de trigger uit 0015 aan een échte delete hangt, werd bij het
 *     vertrek van de laatste ouder de toestemming niet ingetrokken en bleven
 *     de toetsuitslagen bewaard.
 *
 * Een knop die zegt dat hij iets verbreekt en dat niet doet, is erger dan
 * geen knop: de ouder denkt dat hij het geregeld heeft en controleert het
 * niet nog eens.
 *
 * @see supabase/migrations/0023_parent_unlink.sql
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';
export interface UnlinkResult {
  ok: boolean;
  message?: string;
  /** Hoeveel ouders er na deze actie nog aan het kind gekoppeld zijn. */
  remainingParents?: number;
  /**
   * True wanneer dit de laatste ouder was. In dat geval heeft de database de
   * toestemming ingetrokken en de bewaarde toetsuitslagen gewist — dat is een
   * ander verhaal richting de ouder dan "je kijkt niet meer mee".
   */
  wasLastParent?: boolean;
}
export async function unlinkParentFromLearnerOnServer(learnerId: string): Promise<UnlinkResult> {
  const {
    data,
    error
  } = await supabase.rpc('unlink_parent_from_learner', {
    target_learner: learnerId
  });
  if (error) {
    logError('[ParentLinks] Ontkoppelen mislukt', error);
    return {
      ok: false,
      message: 'Ontkoppelen is niet gelukt. Probeer het opnieuw.'
    };
  }
  const res = (data ?? {}) as Record<string, number>;
  const removed = res.removed ?? 0;
  const remaining = res.remaining_parents ?? 0;

  // Nul verwijderde rijen betekent dat de koppeling er serverside al niet
  // (meer) was. Dat is geen storing, maar we melden het wel als mislukt: de
  // lokale weergave klopte kennelijk niet met de server, en dan is "gelukt"
  // zeggen precies de valse geruststelling die we hier proberen weg te halen.
  if (removed === 0) {
    return {
      ok: false,
      message: 'Deze koppeling bestond al niet meer. Ververs de pagina om de actuele stand te zien.'
    };
  }
  return {
    ok: true,
    remainingParents: remaining,
    wasLastParent: remaining === 0
  };
}